import React, { useCallback, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import Loader from "@druva-apps/orion/lib/loader";

import { DEFAULT_REDIRECT_URL, GENERIC_ERROR_MESSAGE } from "consts";
import {
  MFA_SESSION_EXPIRED_MESSAGE,
  MFA_SETUP_AND_AUTHENTICATOR_OTP_TTL_MS,
} from "authSessionConfig";
import {
  NEXT_CHALLENGE,
  COGNITO_MFA_CHALLENGE,
  TENANT_MFA_MODE,
} from "consts/authChallenges";
import { showAlert } from "slices/alertsSlice";
import { getMfaSettings } from "utils/authApi";
import useLogIn from "queryHooks/auth/useLogIn";
import useResendEmail from "queryHooks/auth/useResendEmail";
import useVerify from "queryHooks/auth/useVerify";
import useLoginSetPassword from "queryHooks/auth/useLoginSetPassword";
import useForgotPassword from "queryHooks/auth/useForgotPassword";

import AccountLocked from "./AccountLocked";
import EmailForm from "./EmailForm";
import PasswordForm from "./PasswordForm";
import ForgotPasswordLinkSent from "./ForgotPasswordLinkSent";
import VerificationForm from "./VerificationForm";
import LoginSetPasswordForm from "./LoginSetPasswordForm";
import MfaSetupPanel from "./MfaSetupPanel";
import { useRedirect } from "queryHooks/auth/useRedirectUrl";
import SSOLogin from "./SSOLogin";
import { setSessionCookies } from "utils/cookies";
import styles from "./loginContainer.module.scss";

const inferLoginChallenge = (payload) => {
  if (payload?.nextChallenge) return payload.nextChallenge;
  if (payload?.mfaVerificationToken) return NEXT_CHALLENGE.EMAIL_OTP;
  return null;
};

/** Maps `GET /auth/v1/mfa` `mfaMode` to verification UI; falls back to Cognito hint. */
const otpVariantForMfaRequired = (mfaSettings, cognitoChallenge) => {
  const mode = Number(mfaSettings?.mfaMode);
  if (mode === TENANT_MFA_MODE.SMS) return "sms";
  if (mode === TENANT_MFA_MODE.EMAIL) return "email";
  if (mode === TENANT_MFA_MODE.TOTP) return "totp";
  if (cognitoChallenge === COGNITO_MFA_CHALLENGE.SMS_MFA) return "sms";
  return "totp";
};

const LoginContainer = ({ redirectUrl }) => {
  const dispatch = useDispatch();
  const { mutateAsync: logInAsync, isLoading: loginInIsLoading } = useLogIn();
  const { mutateAsync: verify, isLoading: verifyIsLoading } = useVerify();
  const { mutate: resendEmail, isLoading: resendEmailIsLoading } =
    useResendEmail();
  const {
    mutateAsync: submitLoginSetPassword,
    isLoading: setPasswordIsLoading,
  } = useLoginSetPassword();
  const { mutate: forgotPassword, isLoading: forgotPasswordIsLoading } =
    useForgotPassword();
  const { redirect, redirecting: getRedirectUrlIsLoading } = useRedirect();

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [notUsingSSO, setNotUsingSSO] = useState(false);
  const [mfaVerificationToken, setMfaVerificationToken] = useState("");
  const [tempAccessToken, setTempAccessToken] = useState("");
  const [otpVariant, setOtpVariant] = useState("email");
  const [mfaSetupMode, setMfaSetupMode] = useState("TOTP");
  const [expiredDate, setExpiredDate] = useState();
  const [unlockMoment, setUnlockMoment] = useState();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [mfaPolicyFetchInFlight, setMfaPolicyFetchInFlight] = useState(false);

  const completeLogin = useCallback(
    (accessToken, refreshToken) => {
      setSessionCookies(refreshToken, accessToken);
      redirect(accessToken, redirectUrl);
    },
    [redirect, redirectUrl]
  );

  const applyAuthPayload = useCallback(
    (result, options = {}) => {
      const { mfaSetupSessionExpiresAt } = options;
      const {
        nextChallenge,
        accessToken,
        refreshToken,
        mfaVerificationToken: mfaTok,
        cognitoChallenge,
        mfaSetupMode: apiMfaSetupMode,
      } = result;

      if (
        refreshToken &&
        accessToken &&
        (!nextChallenge || nextChallenge === NEXT_CHALLENGE.NONE)
      ) {
        setIsRedirecting(true);
        completeLogin(accessToken, refreshToken);
        return;
      }

      if (accessToken) {
        setTempAccessToken(accessToken);
      }
      if (mfaTok != null && mfaTok !== "") {
        setMfaVerificationToken(mfaTok);
      }
      if (apiMfaSetupMode === "SMS" || apiMfaSetupMode === 2) {
        setMfaSetupMode("SMS");
      } else if (apiMfaSetupMode === "TOTP" || apiMfaSetupMode === 1) {
        setMfaSetupMode("TOTP");
      }

      const challenge = nextChallenge || inferLoginChallenge(result);

      if (
        challenge === NEXT_CHALLENGE.MFA_SETUP_REQUIRED &&
        result.mfaSettings
      ) {
        const mode = Number(result.mfaSettings?.mfaMode);
        if (mode === TENANT_MFA_MODE.SMS) {
          setMfaSetupMode("SMS");
        } else if (mode === TENANT_MFA_MODE.TOTP) {
          setMfaSetupMode("TOTP");
        }
      }

      switch (challenge) {
        case NEXT_CHALLENGE.EMAIL_OTP:
          setOtpVariant("email");
          setExpiredDate(Date.now() + 300000);
          setStep(3);
          break;
        case NEXT_CHALLENGE.MFA_REQUIRED: {
          const variant = otpVariantForMfaRequired(
            result.mfaSettings,
            cognitoChallenge
          );
          setOtpVariant(variant);
          setExpiredDate(
            variant === "sms"
              ? Date.now() + 180000
              : variant === "email"
              ? Date.now() + 300000
              : Date.now() + MFA_SETUP_AND_AUTHENTICATOR_OTP_TTL_MS
          );
          setStep(3);
          break;
        }
        case NEXT_CHALLENGE.NEW_PASSWORD_REQUIRED:
          setExpiredDate(Date.now() + MFA_SETUP_AND_AUTHENTICATOR_OTP_TTL_MS);
          setStep(4);
          break;
        case NEXT_CHALLENGE.MFA_SETUP_REQUIRED:
          // Align with /step1 accessToken TTL. Prefer anchor captured at login response (before MFA policy fetch).
          setExpiredDate(
            mfaSetupSessionExpiresAt ??
              Date.now() + MFA_SETUP_AND_AUTHENTICATOR_OTP_TTL_MS
          );
          setStep(5);
          break;
        default:
          dispatch(
            showAlert({
              message: GENERIC_ERROR_MESSAGE,
              type: "danger",
            })
          );
      }
    },
    [completeLogin, dispatch]
  );

  const submitEmailFormHandler = useCallback(
    ({ email: nextEmail, notUsingSSO: nuo }) => {
      setEmail(nextEmail);
      setNotUsingSSO(nuo);
      setStep(1);
    },
    []
  );

  const handleForgotPassword = useCallback(() => {
    forgotPassword(
      { email },
      {
        onSuccess: () => {
          setStep(6);
        },
        onError: (e) => {
          dispatch(
            showAlert({
              message: e?.message || GENERIC_ERROR_MESSAGE,
              type: "danger",
            })
          );
        },
      }
    );
  }, [dispatch, email, forgotPassword]);

  const handleResendForgotPassword = useCallback(() => {
    forgotPassword(
      { email },
      {
        onSuccess: () => {
          dispatch(
            showAlert({
              message: "Email successfully sent",
              type: "success",
            })
          );
        },
        onError: (e) => {
          dispatch(
            showAlert({
              message: e?.message || GENERIC_ERROR_MESSAGE,
              type: "danger",
            })
          );
        },
      }
    );
  }, [dispatch, email, forgotPassword]);

  const handleGoToLoginFromForgotSent = useCallback(() => {
    setStep(0);
  }, []);

  const enrichWithMfaPolicyIfRequired = useCallback(async (payload) => {
    const needsTenantMfaPolicy =
      (payload?.nextChallenge === NEXT_CHALLENGE.MFA_REQUIRED ||
        payload?.nextChallenge === NEXT_CHALLENGE.MFA_SETUP_REQUIRED) &&
      payload?.accessToken;
    if (!needsTenantMfaPolicy) {
      return payload;
    }
    setMfaPolicyFetchInFlight(true);
    try {
      const mfaSettings = await getMfaSettings(payload.accessToken);
      return { ...payload, mfaSettings };
    } finally {
      setMfaPolicyFetchInFlight(false);
    }
  }, []);

  const submitPasswordFormHandler = useCallback(
    async ({ password: pwd }) => {
      try {
        const result = await logInAsync({ email, password: pwd });
        const challengeNow =
          result?.nextChallenge || inferLoginChallenge(result);
        const mfaSetupSessionExpiresAt =
          challengeNow === NEXT_CHALLENGE.MFA_SETUP_REQUIRED &&
          result?.accessToken
            ? Date.now() + MFA_SETUP_AND_AUTHENTICATOR_OTP_TTL_MS
            : undefined;
        ReactDOM.unstable_batchedUpdates(() => {
          setLoginPassword(pwd);
        });
        try {
          const enriched = await enrichWithMfaPolicyIfRequired(result);
          applyAuthPayload(enriched, { mfaSetupSessionExpiresAt });
        } catch (fetchErr) {
          dispatch(
            showAlert({
              message: fetchErr.message || GENERIC_ERROR_MESSAGE,
              type: "danger",
            })
          );
        }
      } catch (e) {
        if (e?.message === "account locked") {
          ReactDOM.unstable_batchedUpdates(() => {
            setUnlockMoment(e?.unlockMoment);
            setStep(-1);
          });
        } else {
          dispatch(
            showAlert({
              message: e?.message || GENERIC_ERROR_MESSAGE,
              type: "danger",
            })
          );
        }
      }
    },
    [
      applyAuthPayload,
      dispatch,
      email,
      enrichWithMfaPolicyIfRequired,
      logInAsync,
    ]
  );

  const submitVerificationFormHandler = useCallback(
    async ({ mfaCode }) => {
      try {
        const result = await verify({
          mfaVerificationToken,
          mfaCode,
          accessToken: tempAccessToken,
        });
        try {
          const enriched = await enrichWithMfaPolicyIfRequired(result);
          applyAuthPayload(enriched);
        } catch (fetchErr) {
          dispatch(
            showAlert({
              message: fetchErr.message || GENERIC_ERROR_MESSAGE,
              type: "danger",
            })
          );
        }
      } catch (e) {
        if (e.code === "GoAuth-1041") {
          ReactDOM.unstable_batchedUpdates(() => {
            setStep(-1);
          });
        } else {
          dispatch(
            showAlert({
              message: e.message || GENERIC_ERROR_MESSAGE,
              type: "danger",
            })
          );
        }
      }
    },
    [
      applyAuthPayload,
      dispatch,
      enrichWithMfaPolicyIfRequired,
      mfaVerificationToken,
      tempAccessToken,
      verify,
    ]
  );

  const resendEmailHandler = useCallback(() => {
    resendEmail(
      {
        password: loginPassword,
        mfaVerificationToken,
        accessToken: tempAccessToken || undefined,
      },
      {
        onSuccess: (result) => {
          if (result?.mfaVerificationToken) {
            setMfaVerificationToken(result.mfaVerificationToken);
          }
          if (result?.accessToken) {
            setTempAccessToken(result.accessToken);
          }
          setExpiredDate(Date.now() + 300000);
          dispatch(
            showAlert({
              message: "Email successfully sent",
              type: "success",
            })
          );
        },
        onError: (e) => {
          dispatch(
            showAlert({
              message: e.message || GENERIC_ERROR_MESSAGE,
              type: "danger",
            })
          );
        },
      }
    );
  }, [
    dispatch,
    loginPassword,
    mfaVerificationToken,
    resendEmail,
    tempAccessToken,
  ]);

  const submitSetPasswordHandler = useCallback(
    async (values) => {
      try {
        const result = await submitLoginSetPassword({
          accessToken: tempAccessToken,
          newPassword: values.password,
          confirmPassword: values.repeatedPassword,
        });
        try {
          const enriched = await enrichWithMfaPolicyIfRequired(result);
          applyAuthPayload(enriched);
        } catch (fetchErr) {
          dispatch(
            showAlert({
              message: fetchErr.message || GENERIC_ERROR_MESSAGE,
              type: "danger",
            })
          );
        }
      } catch (e) {
        dispatch(
          showAlert({
            message: e.message || GENERIC_ERROR_MESSAGE,
            type: "danger",
          })
        );
      }
    },
    [
      applyAuthPayload,
      dispatch,
      enrichWithMfaPolicyIfRequired,
      submitLoginSetPassword,
      tempAccessToken,
    ]
  );

  const otpInstruction = {
    email:
      "Enter the verification OTP that we have sent to your registered email address",
    totp: "Enter OTP generated on the authenticator app",
    sms: "Enter OTP sent on your registered mobile number",
  }[otpVariant];

  const otpSessionLabel =
    otpVariant === "email"
      ? "The verification code expires in"
      : otpVariant === "sms"
      ? "The activation OTP will expire in"
      : "The session will expire in";

  const otpResendLabel =
    otpVariant === "email"
      ? "Resend Email"
      : otpVariant === "sms"
      ? "Resend OTP"
      : null;

  const goEmailStep = useCallback(() => setStep(0), []);

  if (
    loginInIsLoading ||
    verifyIsLoading ||
    mfaPolicyFetchInFlight ||
    getRedirectUrlIsLoading ||
    resendEmailIsLoading ||
    setPasswordIsLoading ||
    isRedirecting
  ) {
    return (
      <div
        className="oui-card-body oui-d-flex oui-align-items-center oui-justify-content-center"
        data-testid="loader-wrapper"
      >
        <Loader isLoading>
          <div className={styles.login_loader} />
        </Loader>
      </div>
    );
  }

  if (step === 0) {
    return (
      <EmailForm
        isInitialValid={!!email}
        onSubmit={submitEmailFormHandler}
        initialValue={email}
      />
    );
  }
  if (step === 1) {
    return (
      <SSOLogin
        setStep={setStep}
        redirectUrl={redirectUrl}
        email={email}
        notUsingSSO={notUsingSSO}
      />
    );
  }
  if (step === 2) {
    return (
      <PasswordForm
        setStep={setStep}
        onSubmit={submitPasswordFormHandler}
        onForgotPassword={handleForgotPassword}
        isForgotPasswordLoading={forgotPasswordIsLoading}
        initialValue=""
        email={email}
      />
    );
  }
  if (step === 6) {
    return (
      <ForgotPasswordLinkSent
        onResend={handleResendForgotPassword}
        onGoToLogin={handleGoToLoginFromForgotSent}
        isResending={forgotPasswordIsLoading}
      />
    );
  }
  if (step === 3) {
    return (
      <VerificationForm
        setStep={setStep}
        onSubmit={submitVerificationFormHandler}
        onResendEmail={resendEmailHandler}
        onResend={otpVariant === "email" ? resendEmailHandler : undefined}
        instructionText={otpInstruction}
        sessionExpireLabel={otpSessionLabel}
        resendLabel={otpResendLabel}
        showDidNotReceiveLink={otpVariant === "email"}
        expiredDate={expiredDate}
        codeExpiredMessage={
          otpVariant === "email" ? undefined : MFA_SESSION_EXPIRED_MESSAGE
        }
        onBack={otpVariant === "totp" ? goEmailStep : undefined}
        backLabel={otpVariant === "totp" ? "Go to Login" : undefined}
      />
    );
  }
  if (step === 4) {
    return (
      <LoginSetPasswordForm
        email={email}
        submitLabel="Change Password"
        onSubmit={submitSetPasswordHandler}
        onBack={goEmailStep}
        expiredDate={expiredDate}
        sessionExpireLabel="The session will expire in"
        codeExpiredMessage={MFA_SESSION_EXPIRED_MESSAGE}
      />
    );
  }
  if (step === 5) {
    return (
      <MfaSetupPanel
        accessToken={tempAccessToken}
        email={email}
        mfaSetupMode={mfaSetupMode}
        sessionExpiresAt={expiredDate}
        onComplete={(result) => applyAuthPayload(result)}
        onBack={goEmailStep}
      />
    );
  }
  if (step === -1) {
    return <AccountLocked unlockMoment={unlockMoment} />;
  }

  return null;
};

LoginContainer.defaultProps = {
  redirectUrl: DEFAULT_REDIRECT_URL,
};

export default LoginContainer;
